const BRAND = {
  olive: "#757B2B",
  oliveDark: "#5F641F",
  oliveLight: "#8E9440",
  coral: "#F3694D",
  cream: "#FBF7E8",
  warmBg: "#F7F5EF",
  ink: "#1E2218",
  ink2: "#5E6553",
  divider: "rgba(30,34,24,0.10)",
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatQty = (value) =>
  toNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });

const buildPresentationLabel = (item) => {
  const presentationName = item?.presentation_name || "-";
  return presentationName;
};

const getBranchLegalName = (note) =>
  note?.branch_legal_name ||
  note?.branch_business_name ||
  note?.branch?.legalName ||
  note?.branch?.business_name ||
  note?.branch?.name ||
  note?.branch_name ||
  "-";

export function printInboundNote(note) {
  if (!note || typeof window === "undefined") return;

  const printWindow = window.open("", "_blank", "width=1120,height=760");
  if (!printWindow) return;

  const items = Array.isArray(note?.items) ? note.items : [];
  const totalLines = items.length;

  const rowsHtml = items.length
    ? items
        .map(
          (item, index) => `
            <tr>
              <td class="col-no">${index + 1}</td>
              <td class="col-product">
                <div class="product-name">${escapeHtml(item?.product_description || "-")}</div>
                <div class="product-code">${escapeHtml(item?.product_code || "Sin código")}</div>
              </td>
              <td class="col-presentation">${escapeHtml(buildPresentationLabel(item))}</td>
              <td class="col-qty text-right">${escapeHtml(formatQty(item?.qty_presentation))}</td>
            </tr>
          `
        )
        .join("")
    : `
        <tr>
          <td colspan="4" class="empty">Sin detalle disponible.</td>
        </tr>
      `;

  const html = `
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${escapeHtml(note?.reference || `NOTA-${note?.id || ""}`)}</title>
        <style>
          @page {
            size: 8.5in 5.5in landscape;
            margin: 8mm;
          }

          :root {
            --olive: ${BRAND.olive};
            --olive-dark: ${BRAND.oliveDark};
            --olive-light: ${BRAND.oliveLight};
            --coral: ${BRAND.coral};
            --cream: ${BRAND.cream};
            --warm-bg: ${BRAND.warmBg};
            --ink: ${BRAND.ink};
            --ink-2: ${BRAND.ink2};
            --divider: ${BRAND.divider};
          }

          * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #ffffff;
            color: var(--ink);
            font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
          }

          body {
            font-size: 10px;
            line-height: 1.32;
          }

          .document {
            width: 100%;
          }

          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 12px;
            padding-bottom: 7px;
            margin-bottom: 8px;
            border-bottom: 1px solid var(--divider);
          }

          .header-left {
            min-width: 0;
            flex: 1 1 auto;
          }

          .eyebrow {
            margin: 0 0 2px;
            font-size: 8px;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: var(--olive-dark);
          }

          .title {
            margin: 0;
            font-size: 18px;
            line-height: 1;
            font-weight: 800;
            color: var(--ink);
          }

          .subtitle {
            margin: 3px 0 0;
            font-size: 9px;
            color: var(--ink-2);
          }

          .ref-box {
            flex: 0 0 170px;
            border: 1px solid var(--olive);
            background: var(--cream);
            padding: 7px 9px;
            text-align: right;
          }

          .ref-label {
            display: block;
            margin-bottom: 2px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--olive-dark);
          }

          .ref-value {
            display: block;
            font-size: 12px;
            font-weight: 800;
            color: var(--ink);
            word-break: break-word;
          }

          .meta-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 4px 10px;
            margin-bottom: 7px;
            padding: 6px 8px;
            background: var(--warm-bg);
            border: 1px solid var(--divider);
          }

          .meta-item {
            min-width: 0;
          }

          .meta-label {
            display: block;
            margin-bottom: 1px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: var(--olive-dark);
          }

          .meta-value {
            display: block;
            font-size: 9px;
            font-weight: 600;
            color: var(--ink);
            word-break: break-word;
          }

          .comment {
            margin-bottom: 8px;
            padding: 6px 8px;
            border: 1px solid var(--divider);
            background: #fff;
          }

          .comment .meta-label {
            margin-bottom: 2px;
          }

          .comment-text {
            margin: 0;
            color: var(--ink-2);
            white-space: pre-wrap;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
            page-break-inside: auto;
          }

          thead {
            display: table-header-group;
          }

          tfoot {
            display: table-footer-group;
          }

          tr {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          thead th {
            padding: 5px 6px;
            background: var(--olive);
            color: #fff;
            text-align: left;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            border: 1px solid var(--oliveDark, var(--olive-dark));
          }

          tbody td {
            padding: 5px 6px;
            border: 1px solid var(--divider);
            vertical-align: top;
            font-size: 9px;
            color: var(--ink);
          }

          tbody tr:nth-child(even) td {
            background: rgba(251, 247, 232, 0.42);
          }

          .col-no {
            width: 34px;
            text-align: center;
          }

          .col-product {
            width: auto;
          }

          .col-presentation {
            width: 170px;
          }

          .col-qty {
            width: 92px;
            white-space: nowrap;
          }

          .product-name {
            font-weight: 700;
            color: var(--ink);
            word-break: break-word;
          }

          .product-code {
            margin-top: 1px;
            font-size: 8px;
            color: var(--ink-2);
          }

          .text-right {
            text-align: right;
          }

          .empty {
            padding: 12px 8px;
            text-align: center;
            color: var(--ink-2);
          }

          .totals {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 7px;
          }

          .totals-item {
            min-width: 120px;
            border: 1px solid var(--divider);
            padding: 5px 7px;
            background: var(--warm-bg);
          }

          .totals-label {
            display: block;
            margin-bottom: 1px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: var(--olive-dark);
          }

          .totals-value {
            display: block;
            font-size: 11px;
            font-weight: 800;
            color: var(--ink);
          }

          .footer-block {
            margin-top: 10px;
            break-inside: avoid;
            page-break-inside: avoid;
            border-top: 1px solid var(--divider);
            padding-top: 8px;
          }

          .footer-grid {
            display: grid;
            grid-template-columns: 1.25fr 1fr;
            gap: 12px;
          }

          .legend {
            padding: 7px 8px;
            background: var(--warm-bg);
            border: 1px solid var(--divider);
            font-size: 9px;
            line-height: 1.4;
            color: var(--ink);
          }

          .signature {
            border: 1px solid var(--divider);
            padding: 7px 8px;
            background: #fff;
          }

          .signature-title {
            margin: 0 0 6px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: var(--olive-dark);
          }

          .signature-row {
            margin-bottom: 8px;
          }

          .signature-line {
            display: block;
            height: 15px;
            border-bottom: 1px solid rgba(30,34,24,0.45);
            margin-bottom: 2px;
          }

          .signature-label {
            display: block;
            font-size: 8px;
            text-transform: uppercase;
            color: var(--ink-2);
          }

          @media print {
            html,
            body {
              background: #fff;
            }

            .document {
              width: 100%;
            }

            a {
              color: inherit;
              text-decoration: none;
            }
          }
        </style>
      </head>
      <body>
        <main class="document">
          <section class="header">
            <div class="header-left">
              <p class="eyebrow">Comprobante interno de inventario</p>
              <h1 class="title">NOTA DE ENTRADA</h1>
              <p class="subtitle">Documento de recepción y control de ingreso de productos</p>
            </div>

            <div class="ref-box">
              <span class="ref-label">Referencia</span>
              <span class="ref-value">${escapeHtml(note?.reference || `NOTA ${note?.id || ""}`)}</span>
            </div>
          </section>

          <section class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">Razón social</span>
              <span class="meta-value">${escapeHtml(getBranchLegalName(note))}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Fecha de creación</span>
              <span class="meta-value">${escapeHtml(formatDateTime(note?.created_at))}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Estado</span>
              <span class="meta-value">${escapeHtml(note?.status || "-")}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Creado por</span>
              <span class="meta-value">${escapeHtml(note?.created_by_name || "-")}</span>
            </div>
          </section>

          <section class="comment">
            <span class="meta-label">Comentario / observación</span>
            <p class="comment-text">${escapeHtml(note?.comment || "-")}</p>
          </section>

          <section>
            <table>
              <thead>
                <tr>
                  <th style="width: 34px;">No.</th>
                  <th>Producto</th>
                  <th style="width: 170px;">Presentación</th>
                  <th style="width: 92px;">Cantidad</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </section>

          <section class="totals">
            <div class="totals-item">
              <span class="totals-label">Total líneas</span>
              <span class="totals-value">${escapeHtml(String(totalLines))}</span>
            </div>
          </section>

          <section class="footer-block">
            <div class="footer-grid">
              <div class="legend">
                Declaro haber recibido a mi entera conformidad los productos descritos en la presente Nota de Entrada.
                Los mismos quedan bajo mi custodia y responsabilidad para su correcto resguardo, manejo y registro
                dentro del inventario de la empresa.
              </div>

              <div class="signature">
                <p class="signature-title">Recepción y conformidad</p>

                <div class="signature-row">
                  <span class="signature-line"></span>
                  <span class="signature-label">Nombre completo</span>
                </div>

                <div class="signature-row">
                  <span class="signature-line"></span>
                  <span class="signature-label">DPI</span>
                </div>

                <div class="signature-row">
                  <span class="signature-line"></span>
                  <span class="signature-label">Cargo</span>
                </div>

                <div class="signature-row" style="margin-bottom: 0;">
                  <span class="signature-line"></span>
                  <span class="signature-label">Firma</span>
                </div>
              </div>
            </div>
          </section>
        </main>
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  printWindow.focus();
  printWindow.setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 250);
}

export default printInboundNote;
